<template>
  <teleport to="body">
    <transition name="drawer-overlay">
      <div
        v-if="visible"
        class="drawer-overlay"
        @click.self="close"
      >
        <transition name="drawer-slide" appear>
          <aside
            v-if="visible"
            class="drawer-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="location-drawer-title"
          >
            <!-- HEADER -->
            <header class="drawer-header">
              <div class="header-main">
                <div class="header-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 21C12 21 19 14.6 19 8.8C19 5.04 15.87 2 12 2C8.13 2 5 5.04 5 8.8C5 14.6 12 21 12 21Z"
                      stroke="currentColor"
                      stroke-width="1.65"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />

                    <circle
                      cx="12"
                      cy="8.8"
                      r="2.4"
                      stroke="currentColor"
                      stroke-width="1.65"
                    />
                  </svg>
                </div>

                <div class="header-text">
                  <div class="header-title-row">
                    <h2
                      id="location-drawer-title"
                      class="drawer-title"
                    >
                      Изменить локацию
                    </h2>

                    <span class="location-badge">
                      Город
                    </span>
                  </div>

                  <p class="drawer-subtitle">
                    Локация влияет на результаты поиска,
                    рекомендации и предложения рядом с вами
                  </p>
                </div>
              </div>

              <button
                class="close-button"
                type="button"
                aria-label="Закрыть"
                @click="close"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M7 7L17 17M17 7L7 17"
                    stroke="currentColor"
                    stroke-width="1.8"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
            </header>

            <!-- SEARCH -->
            <div class="search-section">
              <div
                class="search-field"
                :class="{
                  focused: isSearchFocused
                }"
              >
                <span class="search-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="10.7"
                      cy="10.7"
                      r="5.7"
                      stroke="currentColor"
                      stroke-width="1.7"
                    />

                    <path
                      d="M15 15L19.3 19.3"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />
                  </svg>
                </span>

                <input
                  v-model="searchQuery"
                  class="search-input"
                  type="text"
                  placeholder="Найти город"
                  autocomplete="off"
                  @focus="isSearchFocused = true"
                  @blur="isSearchFocused = false"
                />

                <button
                  v-if="searchQuery"
                  class="clear-search"
                  type="button"
                  aria-label="Очистить поиск"
                  @click="searchQuery = ''"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M6 6L14 14M14 6L6 14"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <!-- BODY -->
            <div
              ref="containerRef"
              class="drawer-body"
            >
              <!-- LOADING -->
              <div
                v-if="isLoading"
                class="loading-state"
              >
                <div class="loading-orbit">
                  <div class="spinner" />
                </div>

                <p class="loading-title">
                  Загружаем города
                </p>

                <p class="loading-subtitle">
                  Это займёт всего пару секунд
                </p>
              </div>

              <!-- ERROR -->
              <div
                v-else-if="loadError"
                class="empty-state"
              >
                <div class="empty-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M12 8V12.5"
                      stroke="currentColor"
                      stroke-width="1.7"
                      stroke-linecap="round"
                    />

                    <circle
                      cx="12"
                      cy="16"
                      r="1"
                      fill="currentColor"
                    />

                    <path
                      d="M10.2 4.1L3.3 16.3C2.6 17.6 3.5 19.2 5 19.2H19C20.5 19.2 21.4 17.6 20.7 16.3L13.8 4.1C13 2.7 11 2.7 10.2 4.1Z"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>

                <p class="empty-title">
                  Не удалось загрузить города
                </p>

                <p class="empty-subtitle">
                  Проверьте соединение и попробуйте ещё раз
                </p>

                <button
                  class="retry-button"
                  type="button"
                  @click="loadCities"
                >
                  Попробовать снова
                </button>
              </div>

              <!-- EMPTY SEARCH -->
              <div
                v-else-if="groupedRegions.length === 0"
                class="empty-state"
              >
                <div class="empty-icon">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <circle
                      cx="10.5"
                      cy="10.5"
                      r="5.5"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="M14.7 14.7L19 19"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>

                <p class="empty-title">
                  Город не найден
                </p>

                <p class="empty-subtitle">
                  Попробуйте изменить запрос
                </p>
              </div>

              <!-- CITIES -->
              <div
                v-else
                class="cities-list"
              >
                <section
                  v-for="region in groupedRegions"
                  :key="region.name"
                  class="region-block"
                >
                  <div class="region-header">
                    <div class="region-main">
                      <div
                        v-if="region.items[0]?.flag"
                        class="flag-wrapper"
                      >
                        <img
                          :src="region.items[0].flag"
                          class="region-flag"
                          alt=""
                        />
                      </div>

                      <div class="region-info">
                        <h3 class="region-title">
                          {{ region.name }}
                        </h3>

                        <span class="region-count">
                          {{ region.items.length }}
                          {{ cityCountLabel(region.items.length) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div class="region-cities">
                    <button
                      v-for="city in region.items"
                      :key="city._id"
                      class="city-item"
                      :class="{
                        selected:
                          selectedCity?._id === city._id
                      }"
                      type="button"
                      @click="selectCity(city)"
                    >
                      <span class="city-name">
                        {{ city.city }}
                      </span>

                      <span
                        v-if="
                          selectedCity?._id === city._id
                        "
                        class="selected-check"
                      >
                        <svg
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M5.5 10.3L8.5 13.2L14.7 6.8"
                            stroke="currentColor"
                            stroke-width="1.8"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                    </button>
                  </div>
                </section>
              </div>
            </div>

            <!-- FOOTER -->
            <footer class="drawer-footer">
              <div class="footer-selection">
                <span class="footer-label">
                  Выбрано
                </span>

                <div
                  v-if="selectedCity"
                  class="selected-location"
                >
                  <span class="selected-location-dot" />

                  <span class="selected-location-name">
                    {{ selectedCity.city }}
                  </span>
                </div>

                <span
                  v-else
                  class="selected-location-empty"
                >
                  Выберите город
                </span>
              </div>

              <button
                class="save-btn"
                type="button"
                :disabled="
                  !selectedCity ||
                  isSaving
                "
                @click="handleSaveCity"
              >
                <span>
                  Сохранить
                </span>

                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M4.5 10H15.5M11 5.5L15.5 10L11 14.5"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </footer>

            <!-- SAVING -->
            <transition name="saving-fade">
              <div
                v-if="isSaving"
                class="saving-overlay"
              >
                <div class="saving-card">
                  <div class="saving-spinner">
                    <div class="spinner" />
                  </div>

                  <div class="saving-text">
                    <p class="saving-title">
                      Сохраняем город
                    </p>

                    <p class="saving-subtitle">
                      Обновляем ваши рекомендации
                    </p>
                  </div>
                </div>
              </div>
            </transition>
          </aside>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";

import CitiesService from "../../api/CitiesService.js";
import UserService from "../../api/UserService.js";
import RegionService from "../../utils/RegionService.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },

  modelValue: {
    type: Object,
    default: null
  }
});

const emit = defineEmits([
  "close",
  "update:modelValue"
]);

const cities = ref([]);
const selectedCity = ref(null);

const searchQuery = ref("");
const isSearchFocused = ref(false);

const isLoading = ref(false);
const isSaving = ref(false);
const loadError = ref(false);

const containerRef = ref(null);

let previousBodyOverflow = "";

const normalizedSearchQuery = computed(() =>
  searchQuery.value
    .trim()
    .toLowerCase()
);

const filteredCities = computed(() => {
  if (!normalizedSearchQuery.value) {
    return cities.value;
  }

  return cities.value.filter(city => {
    const cityName =
      String(city.city || "")
        .toLowerCase();

    const regionName =
      String(city.region_name || "")
        .toLowerCase();

    return (
      cityName.includes(
        normalizedSearchQuery.value
      ) ||
      regionName.includes(
        normalizedSearchQuery.value
      )
    );
  });
});

const groupedRegions = computed(() => {
  const groups = new Map();

  filteredCities.value.forEach(city => {
    const region =
      city.region_name ||
      "Другие регионы";

    if (!groups.has(region)) {
      groups.set(region, []);
    }

    groups
      .get(region)
      .push(city);
  });

  return Array
    .from(groups.entries())
    .map(([name, items]) => ({
      name,
      items
    }));
});

async function loadCities() {
  if (isLoading.value) {
    return;
  }

  isLoading.value = true;
  loadError.value = false;

  try {
    const data =
      await CitiesService.fetchCities();

    cities.value =
      (Array.isArray(data) ? data : [])
        .map(city => ({
          ...city,

          flag:
            RegionService.getFlagUrl(
              city.region_id
            )
        }));

    syncSelectedCity();
  } catch (error) {
    console.error(
      "❌ Ошибка загрузки городов:",
      error
    );

    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

function syncSelectedCity() {
  if (!props.modelValue) {
    return;
  }

  const modelId =
    props.modelValue._id ||
    props.modelValue.id;

  if (!modelId) {
    return;
  }

  const matchedCity =
    cities.value.find(
      city =>
        city._id === modelId
    );

  if (matchedCity) {
    selectedCity.value =
      matchedCity;
  }
}

function selectCity(city) {
  selectedCity.value = city;
}

async function handleSaveCity() {
  if (
    !selectedCity.value ||
    isSaving.value
  ) {
    return;
  }

  isSaving.value = true;

  const payload = {
    id:
      selectedCity.value._id,

    name:
      selectedCity.value.city,

    region_id:
      selectedCity.value.region_id,

    region_name:
      selectedCity.value.region_name
  };

  try {
    await UserService
      .updateUserCity(payload);

    localStorage.setItem(
      "selected_city",
      JSON.stringify(payload)
    );

    emit(
      "update:modelValue",
      selectedCity.value
    );

    emit("close");
  } catch (error) {
    console.error(
      "❌ Ошибка сохранения города:",
      error
    );
  } finally {
    isSaving.value = false;
  }
}

function cityCountLabel(count) {
  const value =
    Math.abs(count) % 100;

  const last =
    value % 10;

  if (
    value > 10 &&
    value < 20
  ) {
    return "городов";
  }

  if (last === 1) {
    return "город";
  }

  if (
    last >= 2 &&
    last <= 4
  ) {
    return "города";
  }

  return "городов";
}

function close() {
  if (isSaving.value) {
    return;
  }

  emit("close");
}

function handleKeydown(event) {
  if (
    event.key === "Escape" &&
    props.visible &&
    !isSaving.value
  ) {
    close();
  }
}

watch(
  () => props.visible,
  async visible => {
    if (visible) {
      searchQuery.value = "";

      previousBodyOverflow =
        document.body.style.overflow;

      document.body.style.overflow =
        "hidden";

      syncSelectedCity();

      await nextTick();

      if (containerRef.value) {
        containerRef.value.scrollTop = 0;
      }

      return;
    }

    document.body.style.overflow =
      previousBodyOverflow;
  }
);

watch(
  () => props.modelValue,
  () => {
    syncSelectedCity();
  },
  {
    deep: true
  }
);

onMounted(() => {
  window.addEventListener(
    "keydown",
    handleKeydown
  );

  loadCities();
});

onBeforeUnmount(() => {
  window.removeEventListener(
    "keydown",
    handleKeydown
  );

  document.body.style.overflow =
    previousBodyOverflow;
});
</script>

<style scoped>
.drawer-overlay {
  position: fixed;
  z-index: 1000;

  inset: 0;

  display: flex;

  background:
    rgba(18, 22, 29, 0.42);

  backdrop-filter:
    blur(7px);

  -webkit-backdrop-filter:
    blur(7px);
}

/* ========================================================= */
/* DRAWER */
/* ========================================================= */

.drawer-content {
  position: relative;

  width: 520px;
  max-width: calc(100vw - 24px);
  height: 100%;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #fbfcfd 100%
    );

  border-right:
    1px solid
    rgba(25, 40, 60, 0.08);

  border-radius:
    0 28px 28px 0;

  box-shadow:
    24px 0 60px
    rgba(12, 20, 34, 0.17);
}

/* ========================================================= */
/* HEADER */
/* ========================================================= */

.drawer-header {
  position: relative;
  z-index: 5;

  display: flex;
  align-items: flex-start;
  justify-content: space-between;

  gap: 20px;

  padding:
    28px 28px 22px;

  border-bottom:
    1px solid
    rgba(20, 35, 55, 0.065);

  background:
    rgba(255, 255, 255, 0.88);

  backdrop-filter:
    blur(18px);

  -webkit-backdrop-filter:
    blur(18px);
}

.header-main {
  min-width: 0;

  display: flex;
  align-items: flex-start;

  gap: 15px;
}

.header-icon {
  width: 42px;
  height: 42px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border:
    1px solid
    rgba(40, 135, 247, 0.1);

  border-radius: 14px;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.12),
      rgba(40, 135, 247, 0.055)
    );

  color:
    #2887f7;

  box-shadow:
    inset 0 1px 0
    rgba(255, 255, 255, 0.8);
}

.header-icon svg {
  width: 22px;
  height: 22px;
}

.header-text {
  min-width: 0;
}

.header-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 8px;
}

.drawer-title {
  margin: 1px 0 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 18px;
  font-weight: 700;
  line-height: 1.15;

  letter-spacing:
    -0.35px;

  color:
    #20242a;
}

.location-badge {
  min-height: 19px;

  display: inline-flex;
  align-items: center;

  padding:
    0 7px;

  border-radius:
    999px;

  background:
    rgba(40, 135, 247, 0.09);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  color:
    #2887f7;
}

.drawer-subtitle {
  max-width: 350px;

  margin:
    7px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 11px;
  line-height: 1.45;

  color:
    #858b93;
}

.close-button {
  width: 36px;
  height: 36px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border:
    1px solid
    rgba(30, 40, 55, 0.07);

  border-radius: 12px;

  background:
    #f6f7f8;

  color:
    #6f757d;

  cursor: pointer;

  transition:
    color 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease,
    transform 0.18s ease;
}

.close-button svg {
  width: 19px;
  height: 19px;
}

.close-button:hover {
  color:
    #292d32;

  background:
    #eef1f4;

  border-color:
    rgba(30, 40, 55, 0.1);

  transform:
    rotate(3deg);
}

/* ========================================================= */
/* SEARCH */
/* ========================================================= */

.search-section {
  padding:
    18px 28px 12px;

  background:
    inherit;
}

.search-field {
  height: 44px;

  display: flex;
  align-items: center;

  gap: 10px;

  padding:
    0 12px;

  border:
    1px solid
    rgba(30, 45, 65, 0.075);

  border-radius: 14px;

  background:
    #f6f8fa;

  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}

.search-field.focused {
  background:
    #ffffff;

  border-color:
    rgba(40, 135, 247, 0.32);

  box-shadow:
    0 0 0 3px
    rgba(40, 135, 247, 0.07);
}

.search-icon {
  width: 20px;
  height: 20px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color:
    #8e949c;
}

.search-icon svg {
  width: 18px;
  height: 18px;
}

.search-input {
  min-width: 0;

  flex: 1;

  padding: 0;

  outline: none;
  border: none;

  background:
    transparent;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 12px;

  color:
    #30353b;
}

.search-input::placeholder {
  color:
    #a3a8ae;
}

.clear-search {
  width: 27px;
  height: 27px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: none;
  border-radius: 8px;

  background:
    rgba(20, 30, 45, 0.045);

  color:
    #8b9199;

  cursor: pointer;
}

.clear-search svg {
  width: 15px;
  height: 15px;
}

/* ========================================================= */
/* BODY */
/* ========================================================= */

.drawer-body {
  position: relative;

  min-height: 0;

  flex: 1;

  overflow-y: auto;

  padding:
    12px 28px 28px;

  scrollbar-width:
    thin;

  scrollbar-color:
    rgba(90, 100, 115, 0.18)
    transparent;
}

.drawer-body::-webkit-scrollbar {
  width: 6px;
}

.drawer-body::-webkit-scrollbar-track {
  background:
    transparent;
}

.drawer-body::-webkit-scrollbar-thumb {
  border-radius:
    999px;

  background:
    rgba(90, 100, 115, 0.18);
}

/* ========================================================= */
/* REGIONS */
/* ========================================================= */

.cities-list {
  display: flex;
  flex-direction: column;

  gap: 18px;
}

.region-block {
  padding:
    16px;

  border:
    1px solid
    rgba(25, 40, 60, 0.065);

  border-radius:
    18px;

  background:
    rgba(248, 249, 251, 0.72);

  box-shadow:
    inset 0 1px 0
    rgba(255, 255, 255, 0.8);
}

.region-header {
  margin-bottom:
    12px;
}

.region-main {
  display: flex;
  align-items: center;

  gap: 10px;
}

.flag-wrapper {
  width: 34px;
  height: 26px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  overflow: hidden;

  border:
    1px solid
    rgba(20, 30, 45, 0.07);

  border-radius:
    8px;

  background:
    #ffffff;

  box-shadow:
    0 2px 6px
    rgba(20, 30, 45, 0.05);
}

.region-flag {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;
}

.region-info {
  min-width: 0;

  display: flex;
  align-items: baseline;
  flex-wrap: wrap;

  gap: 7px;
}

.region-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;
  font-weight: 700;

  letter-spacing:
    -0.15px;

  color:
    #343940;
}

.region-count {
  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  color:
    #9a9fa6;
}

/* ========================================================= */
/* CITIES */
/* ========================================================= */

.region-cities {
  display: grid;

  grid-template-columns:
    repeat(2, minmax(0, 1fr));

  gap: 7px;
}

.city-item {
  min-width: 0;
  min-height: 39px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 7px;

  padding:
    8px 10px;

  border:
    1px solid
    rgba(30, 45, 65, 0.055);

  border-radius:
    11px;

  background:
    #ffffff;

  color:
    #626a73;

  cursor: pointer;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10.5px;
  text-align: left;

  transition:
    color 0.16s ease,
    background-color 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.16s ease;
}

.city-item:hover {
  color:
    #37485b;

  border-color:
    rgba(40, 135, 247, 0.16);

  background:
    rgba(40, 135, 247, 0.035);

  transform:
    translateY(-1px);
}

.city-item.selected {
  color:
    #216fc8;

  border-color:
    rgba(40, 135, 247, 0.24);

  background:
    rgba(40, 135, 247, 0.085);

  box-shadow:
    inset 0 0 0 1px
    rgba(40, 135, 247, 0.025);
}

.city-name {
  min-width: 0;

  overflow-wrap:
    anywhere;
}

.selected-check {
  width: 18px;
  height: 18px;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius:
    50%;

  background:
    #2887f7;

  color:
    #ffffff;

  box-shadow:
    0 3px 8px
    rgba(40, 135, 247, 0.22);
}

.selected-check svg {
  width: 13px;
  height: 13px;
}

/* ========================================================= */
/* FOOTER */
/* ========================================================= */

.drawer-footer {
  position: relative;
  z-index: 6;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  padding:
    16px 28px 20px;

  border-top:
    1px solid
    rgba(20, 35, 55, 0.065);

  background:
    rgba(255, 255, 255, 0.92);

  backdrop-filter:
    blur(18px);

  -webkit-backdrop-filter:
    blur(18px);
}

.footer-selection {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 4px;
}

.footer-label {
  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 8px;

  text-transform:
    uppercase;

  letter-spacing:
    0.55px;

  color:
    #a2a7ad;
}

.selected-location {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 6px;
}

.selected-location-dot {
  width: 6px;
  height: 6px;

  flex-shrink: 0;

  border-radius:
    50%;

  background:
    #2887f7;

  box-shadow:
    0 0 0 3px
    rgba(40, 135, 247, 0.1);
}

.selected-location-name {
  overflow: hidden;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  color:
    #41474f;

  white-space:
    nowrap;

  text-overflow:
    ellipsis;
}

.selected-location-empty {
  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  color:
    #999fa6;
}

.save-btn {
  min-width: 132px;
  height: 42px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding:
    0 17px;

  border:
    1px solid
    rgba(40, 135, 247, 0.2);

  border-radius:
    13px;

  background:
    linear-gradient(
      135deg,
      #3690f8 0%,
      #247ced 100%
    );

  color:
    #ffffff;

  cursor: pointer;

  box-shadow:
    0 7px 17px
    rgba(40, 135, 247, 0.2);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 11px;

  transition:
    transform 0.17s ease,
    box-shadow 0.17s ease,
    opacity 0.17s ease;
}

.save-btn svg {
  width: 16px;
  height: 16px;
}

.save-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0 10px 22px
    rgba(40, 135, 247, 0.27);
}

.save-btn:active:not(:disabled) {
  transform:
    translateY(0)
    scale(0.985);
}

.save-btn:disabled {
  opacity: 0.38;

  cursor:
    not-allowed;

  box-shadow:
    none;
}

/* ========================================================= */
/* LOADING / EMPTY */
/* ========================================================= */

.loading-state,
.empty-state {
  min-height: 360px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;
}

.loading-orbit,
.empty-icon,
.saving-spinner {
  width: 52px;
  height: 52px;

  display: flex;
  align-items: center;
  justify-content: center;

  margin-bottom: 14px;

  border-radius:
    17px;

  background:
    rgba(40, 135, 247, 0.07);

  color:
    #2887f7;
}

.empty-icon svg {
  width: 25px;
  height: 25px;
}

.spinner {
  width: 24px;
  height: 24px;

  border:
    2.5px solid
    rgba(40, 135, 247, 0.14);

  border-top-color:
    #2887f7;

  border-radius:
    50%;

  animation:
    spin 0.7s linear infinite;
}

.loading-title,
.empty-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 12px;

  color:
    #454a51;
}

.loading-subtitle,
.empty-subtitle {
  margin:
    6px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;
  line-height: 1.45;

  color:
    #92979e;
}

.retry-button {
  margin-top:
    14px;

  padding:
    9px 14px;

  border:
    1px solid
    rgba(40, 135, 247, 0.13);

  border-radius:
    10px;

  background:
    rgba(40, 135, 247, 0.07);

  color:
    #2887f7;

  cursor: pointer;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}

/* ========================================================= */
/* SAVING */
/* ========================================================= */

.saving-overlay {
  position: absolute;
  z-index: 30;

  inset: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  padding:
    28px;

  background:
    rgba(247, 249, 251, 0.72);

  backdrop-filter:
    blur(8px);

  -webkit-backdrop-filter:
    blur(8px);
}

.saving-card {
  min-width: 250px;

  display: flex;
  align-items: center;

  gap: 14px;

  padding:
    16px 18px;

  border:
    1px solid
    rgba(30, 45, 65, 0.08);

  border-radius:
    18px;

  background:
    rgba(255, 255, 255, 0.96);

  box-shadow:
    0 18px 45px
    rgba(20, 30, 45, 0.13);
}

.saving-spinner {
  width: 42px;
  height: 42px;

  flex-shrink: 0;

  margin: 0;

  border-radius:
    13px;
}

.saving-text {
  min-width: 0;
}

.saving-title {
  margin: 0;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 11px;

  color:
    #3b4148;
}

.saving-subtitle {
  margin:
    4px 0 0;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  color:
    #92979e;
}

/* ========================================================= */
/* DARK */
/* ========================================================= */

html[data-theme="dark"] .drawer-overlay {
  background:
    rgba(5, 7, 10, 0.62);
}

html[data-theme="dark"] .drawer-content {
  background:
    linear-gradient(
      180deg,
      #22242a 0%,
      #1d1f24 100%
    );

  border-right-color:
    rgba(255, 255, 255, 0.06);

  box-shadow:
    24px 0 60px
    rgba(0, 0, 0, 0.42);
}

html[data-theme="dark"] .drawer-header,
html[data-theme="dark"] .drawer-footer {
  background:
    rgba(31, 33, 38, 0.9);

  border-color:
    rgba(255, 255, 255, 0.06);
}

html[data-theme="dark"] .header-icon {
  border-color:
    rgba(78, 153, 255, 0.1);

  background:
    rgba(40, 135, 247, 0.1);

  box-shadow:
    inset 0 1px 0
    rgba(255, 255, 255, 0.025);
}

html[data-theme="dark"] .drawer-title {
  color:
    #f3f4f5;
}

html[data-theme="dark"] .drawer-subtitle {
  color:
    #858b94;
}

html[data-theme="dark"] .close-button {
  border-color:
    rgba(255, 255, 255, 0.055);

  background:
    rgba(255, 255, 255, 0.04);

  color:
    #8d929a;
}

html[data-theme="dark"] .close-button:hover {
  background:
    rgba(255, 255, 255, 0.07);

  color:
    #e7e8ea;
}

html[data-theme="dark"] .search-field {
  border-color:
    rgba(255, 255, 255, 0.055);

  background:
    rgba(255, 255, 255, 0.035);
}

html[data-theme="dark"] .search-field.focused {
  border-color:
    rgba(72, 148, 255, 0.28);

  background:
    rgba(255, 255, 255, 0.05);

  box-shadow:
    0 0 0 3px
    rgba(40, 135, 247, 0.065);
}

html[data-theme="dark"] .search-input {
  color:
    #e7e8ea;
}

html[data-theme="dark"] .search-input::placeholder {
  color:
    #686e76;
}

html[data-theme="dark"] .clear-search {
  background:
    rgba(255, 255, 255, 0.045);

  color:
    #757b83;
}

html[data-theme="dark"] .region-block {
  border-color:
    rgba(255, 255, 255, 0.05);

  background:
    rgba(255, 255, 255, 0.022);

  box-shadow:
    inset 0 1px 0
    rgba(255, 255, 255, 0.015);
}

html[data-theme="dark"] .flag-wrapper {
  border-color:
    rgba(255, 255, 255, 0.06);

  background:
    #292b31;

  box-shadow:
    0 2px 7px
    rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .region-title {
  color:
    #d7d9dc;
}

html[data-theme="dark"] .region-count {
  color:
    #676d75;
}

html[data-theme="dark"] .city-item {
  border-color:
    rgba(255, 255, 255, 0.045);

  background:
    rgba(255, 255, 255, 0.028);

  color:
    #979da6;
}

html[data-theme="dark"] .city-item:hover {
  border-color:
    rgba(70, 148, 255, 0.14);

  background:
    rgba(40, 135, 247, 0.055);

  color:
    #c7d2df;
}

html[data-theme="dark"] .city-item.selected {
  border-color:
    rgba(73, 150, 255, 0.2);

  background:
    rgba(40, 135, 247, 0.105);

  color:
    #8ec2ff;
}

html[data-theme="dark"] .footer-label {
  color:
    #626870;
}

html[data-theme="dark"] .selected-location-name {
  color:
    #cdd0d5;
}

html[data-theme="dark"] .selected-location-empty {
  color:
    #666c74;
}

html[data-theme="dark"] .loading-title,
html[data-theme="dark"] .empty-title,
html[data-theme="dark"] .saving-title {
  color:
    #d7dade;
}

html[data-theme="dark"] .loading-subtitle,
html[data-theme="dark"] .empty-subtitle,
html[data-theme="dark"] .saving-subtitle {
  color:
    #747a83;
}

html[data-theme="dark"] .saving-overlay {
  background:
    rgba(22, 24, 28, 0.76);
}

html[data-theme="dark"] .saving-card {
  border-color:
    rgba(255, 255, 255, 0.06);

  background:
    rgba(36, 38, 44, 0.97);

  box-shadow:
    0 18px 45px
    rgba(0, 0, 0, 0.32);
}

/* ========================================================= */
/* ANIMATIONS */
/* ========================================================= */

.drawer-overlay-enter-active,
.drawer-overlay-leave-active {
  transition:
    opacity 0.24s ease;
}

.drawer-overlay-enter-from,
.drawer-overlay-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition:
    transform 0.32s
    cubic-bezier(
      0.22,
      1,
      0.36,
      1
    );
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform:
    translateX(-100%);
}

.saving-fade-enter-active,
.saving-fade-leave-active {
  transition:
    opacity 0.2s ease;
}

.saving-fade-enter-from,
.saving-fade-leave-to {
  opacity: 0;
}

@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}

/* ========================================================= */
/* MOBILE */
/* ========================================================= */

@media (max-width: 620px) {
  .drawer-content {
    width:
      calc(100vw - 12px);

    max-width:
      none;

    border-radius:
      0 24px 24px 0;
  }

  .drawer-header {
    padding:
      22px 20px 18px;
  }

  .search-section {
    padding:
      15px 20px 10px;
  }

  .drawer-body {
    padding:
      10px 20px 24px;
  }

  .drawer-footer {
    padding:
      14px 20px 18px;
  }

  .region-cities {
    grid-template-columns:
      1fr;
  }

  .drawer-title {
    font-size: 16px;
  }

  .drawer-subtitle {
    font-size: 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drawer-overlay-enter-active,
  .drawer-overlay-leave-active,
  .drawer-slide-enter-active,
  .drawer-slide-leave-active,
  .saving-fade-enter-active,
  .saving-fade-leave-active,
  .city-item,
  .close-button,
  .save-btn {
    transition: none;
  }

  .spinner {
    animation-duration:
      1.4s;
  }
}
</style>